import { MainLayout } from '@components/layouts/main.layout';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { AnimatePresence, motion } from 'motion/react';
import { Navigate, useParams } from 'react-router-dom';
import { ReadinessTab } from './components/shared/readiness-tab.component';
import { TechnologyCyberTab } from './components/technology-cyber-tab.component';
import { TechnologyHeader } from './components/technology-header.component';
import { TechnologyMetadataTab } from './components/technology-metadata-tab.component';
import { TechnologyMissionTab } from './components/technology-mission-tab.component';
import { TechnologyOrganizationTab } from './components/technology-organization-tab.component';
import { TechnologyOverviewTab } from './components/technology-overview-tab.component';
import { usePrefetchReadinessData } from './use-prefetch-readiness-data';
import { useTechnologyPage } from './use-technology-page';

export const TechnologyPage = () => {
  const { technologyId } = useParams<{ technologyId: string }>();

  const {
    displayedMissionAssessment,
    helmetTitle,
    isLoadingDisplayedMissionAssessment,
    isLoadingTechnology,
    isTechnologyAccessForbidden,
    isTransitioning,
    isVersionsLoading,
    maVersions,
    onToggleTab,
    selectedTab,
    technology,
    technologyFallbackPath,
  } = useTechnologyPage({ technologyId });

  // Prefetch TRL and MRL data when technology page loads
  usePrefetchReadinessData(technologyId);

  // Always render MainLayout to avoid sidebar blink on navigation
  if (!technologyId) {
    return null;
  }

  if (isTechnologyAccessForbidden) {
    return null;
  }

  if (!isLoadingTechnology && !technology) {
    return <Navigate to={technologyFallbackPath} />;
  }

  // Show loading state inside layout to prevent sidebar unmount
  if (isLoadingTechnology) {
    return (
      <PageviewTracker>
        <MainLayout
          fullWidth
          className="max-w-full"
        >
          <div className="flex flex-col gap-4">
            <div className="h-32 animate-pulse bg-muted rounded-lg" />
            <div className="h-96 animate-pulse bg-muted rounded-lg" />
          </div>
        </MainLayout>
      </PageviewTracker>
    );
  }

  const renderContent = () => {
    switch (selectedTab) {
      case 'overview':
        return (
          <TechnologyOverviewTab
            onNavigateToTab={onToggleTab}
            technologyId={technologyId}
          />
        );
      case 'details':
        return <TechnologyMetadataTab technologyId={technologyId} />;
      case 'cyber':
        if (!technology) return null;
        return <TechnologyCyberTab technology={technology} />;
      case 'organization':
        return <TechnologyOrganizationTab technology={technology} />;
      case 'trl':
        return <ReadinessTab type="trl" />;
      case 'mrl':
        return <ReadinessTab type="mrl" />;
      case 'ma':
        if (!technology) return null;
        return (
          <TechnologyMissionTab
            isLoading={isLoadingDisplayedMissionAssessment}
            isTransitioning={isTransitioning}
            isVersionsLoading={isVersionsLoading}
            missionAssessment={displayedMissionAssessment ?? null}
            onNavigateToTab={onToggleTab}
            technology={technology}
            versions={maVersions}
          />
        );
      default:
        return null;
    }
  };

  return (
    <PageviewTracker>
      <HelmetWrapper title={helmetTitle} />
      <MainLayout
        fullWidth
        className="max-w-full"
      >
        <div className="flex flex-col gap-4">
          {/* Tech Header - full width */}
          <TechnologyHeader
            onToggleTab={onToggleTab}
            selectedTab={selectedTab}
          />

          {/* Content area */}
          <div
            className="@container/technology mx-auto w-full max-w-[1600px] px-4"
            data-technology-container="true"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </MainLayout>
    </PageviewTracker>
  );
};
